import {RtcRequester} from "../../../MobxStores/RequestApi/RtcRequester";
import {RecordingStatusEnum} from "../../../serverClientShared/sharedEnums";
import {
    messageTypeEnum,
    RECORDING_STOP_THRESHOLD,
    RECORDING_STOPPING_TIMEOUT,
    RECORDING_INITIALIZING_TIMEOUT
} from "../core";
import {generalStore, modalStore} from "../../../index";
import Settings from "../../../settings.json";

export class RecordingService {

    recordingId = '';
    recordingSecret = '';
    recordingFileId = '';
    recordingUploadId = '';
    recorderRoomId = '';

    constructor(store, socketManager) {
        this.store = store;
        this.socketManager = socketManager;
        this.recorderHeartbeatTimeout = null;
        this.stoppingTimeout = null;
        this.initializingTimeout = null;
    }

    async startRecording() {
        try {
            this.socketManager.joinRoom(this.store.roomId);
            this.store.recordingStatus = RecordingStatusEnum.INITIALIZING;
            this.store.recordingInitiatorUserId = generalStore.userDetails._id;
            this.store.recordingStartTimestamp = 0;
            this.recorderRoomId = 'recorder_' + this.store.roomId;
            const {recordingSecret} = await RtcRequester.startRecording(generalStore.workspace, this.store.roomId);
            this.recordingSecret = recordingSecret;
            this.resetInitializingTimeout();
        } catch (err) {
            this.store.recordingStatus = '';
            modalStore.showErrorMessage(generalStore.translate('errorStartingRecording'));
        }
    }

    async stopRecording() {
        this.clearTimeout('recorderHeartbeatTimeout');
        this.store.recordingStatus = RecordingStatusEnum.STOPPING;
        this.store.recordingEndTimestamp = Date.now();
        this.resetTimeout('stoppingTimeout', () => this.autoStop(), RECORDING_STOPPING_TIMEOUT);

        const message = {MessageId: messageTypeEnum.STOP_RECORDING};
        await this.socketManager.sendSignedMessage(
            this.recorderRoomId,
            message,
            this.recordingSecret,
            this.store.deviceId
        );
        await RtcRequester.updateRecordingStatus(generalStore.workspace, this.store.roomId, {
            status: RecordingStatusEnum.STOPPING,
        });
    }

    async maximizeUserForRecorder(participantId) {
        const message = {
            MessageId: messageTypeEnum.RSE_MAXIMIZED,
            participantId
        };
        await this.socketManager.sendSignedMessage(
            this.recorderRoomId,
            message,
            this.recordingSecret,
            this.store.deviceId
        );
    }

    async handleRecordingStarted() {
        try {
            await this.socketManager.sendHeartbeat(this.store.roomId, this.store.deviceId, this.store.micOn, this.store.name, generalStore.userDetails);
            this.store.isNextFrameKey = true;

            const recording = await RtcRequester.getActiveRecording(generalStore.workspace, this.store.roomId);
            if (recording.recordingStatus !== RecordingStatusEnum.IN_PROGRESS)
                return;
            this.clearTimeout('initializingTimeout');
            this.recordingId = recording.recordingId;
            this.store.recordingStatus = RecordingStatusEnum.IN_PROGRESS;
            this.store.recordingStartTimestamp = Date.parse(recording.startDate);
            this.store.recordingInitiatorUserId = recording.initiatorId;
            if (this.store.isRecordingInitiator) {
                this.recordingFileId = recording.fileId;
                this.recordingUploadId = recording.uploadId;
                this.socketManager.joinRoom(this.recorderRoomId);
                this.resetTimeout('recorderHeartbeatTimeout', () => this.autoStop(), RECORDING_STOP_THRESHOLD);
                this.store.rtcService.sendMessage(generalStore.translate('RecordingHasStarted'), generalStore.userDetails);
            }
        } catch (error) {
            console.error('handleRecordingStarted: failed to fetch active recording', error);
        }
    }

    async handleRecordingStopped() {
        if (this.store.isRecordingInitiator) {
            this.clearTimeout('recorderHeartbeatTimeout');
            this.clearTimeout('stoppingTimeout');
        }
        try {
            const recording = await RtcRequester.getActiveRecording(generalStore.workspace, this.store.roomId, this.recordingId);
            if (![RecordingStatusEnum.COMPLETED, RecordingStatusEnum.FINALIZING, RecordingStatusEnum.FAILED].includes(recording.recordingStatus))
                return;
            this.store.recordingStatus = recording.recordingStatus;
            if (!this.store.isRecordingInitiator) {
                this.store.recordingEndTimestamp = Date.now();
            } else {
                this.store.rtcService.sendMessage(generalStore.translate('RecordingHasEnded'), generalStore.userDetails);
            }
        } catch (error) {
            console.error('handleRecordingStopped: failed to fetch recording status', error);
        }
    }

    checkExistingRecording(room) {
        const recording = room.Recordings.find(r => this.isStatusActive(r.Status));
        if (!recording)
            return;
        this.recordingId = recording._id;
        this.store.recordingStatus = recording.Status;
        this.store.recordingStartTimestamp = Date.parse(recording.StartDate);
        this.store.recordingInitiatorUserId = recording.InitiatorUserId;
        if (this.store.isRecordingInitiator) {
            this.recordingSecret = recording.Secret;
            this.recorderRoomId = 'recorder_' + room.RoomIdentifier;
            this.socketManager.joinRoom(this.recorderRoomId);
            if (recording.Status !== RecordingStatusEnum.INITIALIZING) {
                this.resetTimeout('recorderHeartbeatTimeout', () => this.autoStop(), RECORDING_STOP_THRESHOLD);
                this.recordingFileId = recording.FileRef.FileId;
                this.recordingUploadId = recording.S3UploadId;
            }
            if (recording.Status === RecordingStatusEnum.INITIALIZING)
                this.resetInitializingTimeout();
            if (recording.Status === RecordingStatusEnum.STOPPING)
                this.resetTimeout('stoppingTimeout', () => this.autoStop(), RECORDING_STOPPING_TIMEOUT);
        }
    }

    isStatusActive(recordingStatus) {
        return [RecordingStatusEnum.INITIALIZING, RecordingStatusEnum.IN_PROGRESS, RecordingStatusEnum.STOPPING].includes(recordingStatus);
    }

    resetInitializingTimeout() {
        this.resetTimeout('initializingTimeout', () => this.handleInitializingTimeout(), RECORDING_INITIALIZING_TIMEOUT);
    }

    resetTimeout(key, callback, delay) {
        if (!this.store.isRecordingInitiator) return;
        this.clearTimeout(key);
        this[key] = setTimeout(callback, delay);
    }

    clearTimeout(key) {
        if (this[key]) {
            clearTimeout(this[key]);
            this[key] = null;
        }
    }

    async handleInitializingTimeout() {
        try {
            await RtcRequester.updateRecordingStatus(generalStore.workspace, this.store.roomId, {
                status: RecordingStatusEnum.FAILED,
            });
        } catch (e) {
            console.error('handleInitializingTimeout: failed to update recording status', e);
        }
        this.store.recordingStatus = '';
        modalStore.showErrorMessage(generalStore.translate('errorStartingRecording'));
    }

    async autoStop() {
        let status = RecordingStatusEnum.FINALIZING;
        this.clearTimeout('recorderHeartbeatTimeout');
        this.clearTimeout('stoppingTimeout');
        this.store.recordingEndTimestamp = Date.now();
        modalStore.isRecordingWarningModalOpen = true;
        try {
            await RtcRequester.updateRecordingStatus(generalStore.workspace, this.store.roomId, {
                status: RecordingStatusEnum.FINALIZING,
                endDate: new Date(),
            });
            await RtcRequester.finalizeRecording(generalStore.workspace, this.store.roomId, this.recordingId);
        } catch (error) {
            console.error('autoStop: failed to finalize recording', error);
            await RtcRequester.updateRecordingStatus(generalStore.workspace, this.store.roomId, {
                status: RecordingStatusEnum.FAILED,
            }).catch(() => {});
            status = RecordingStatusEnum.FAILED;
        }
        this.store.recordingStatus = status;
        this.broadcastStopEvent();
    }

    broadcastStopEvent() {
        const socketMessage = {MessageId: messageTypeEnum.RECORDING_STOPPED};
        this.store.sendRoomMessage(
            JSON.stringify(socketMessage),
            this.store.roomId
        );
    }
}